/* eslint-disable max-lines-per-function */
/**
 * Header Component - Main navigation header with Electron window dragging support
 *
 * ## Electron macOS Window Dragging
 *
 * This component implements a reliable window dragging pattern for frameless Electron windows
 * on macOS. The solution uses inline styles for drag regions to ensure consistent behavior
 * across window focus states.
 *
 * ### Key Architecture Decisions:
 *
 * 1. **Inline Styles for Drag Regions**
 *    - Use `style={{ WebkitAppRegion: 'drag' }}` instead of CSS classes
 *    - Inline styles have highest CSS specificity (1,0,0,0), overriding all class-based rules
 *    - This prevents conflicts with global `.electron-no-drag` rules regardless of focus state
 *
 * 2. **Flex Spacer for Draggable Area**
 *    - Added `<div className="flex-1 min-w-[40px]" style={{ WebkitAppRegion: 'drag' }}/>` between logo and buttons
 *    - Provides dedicated draggable surface without blocking interactive elements
 *    - Alternative overlay approaches blocked button clicks even with pointer-events manipulation
 *
 * 3. **Traffic Light Button Spacing**
 *    - `.electron-traffic-light-spacer` ensures logo doesn't overlap macOS traffic lights
 *    - Traffic lights positioned at ~20px from left, ~52px wide
 *    - Spacer is `display: none` by default, `display: block; width: 40px` on Electron macOS
 *
 * 4. **Pointer Events Hierarchy**
 *    - Header has `pointer-events-auto` to enable interactions
 *    - Individual interactive elements marked with `.electron-no-drag` to prevent dragging
 *    - Drag regions use inline style to override any conflicting pointer-events rules
 *
 * ### Common Pitfalls Avoided:
 * - ❌ Using CSS classes for drag regions (specificity conflicts with focus-dependent rules)
 * - ❌ Absolute positioned overlays (block clicks to buttons underneath)
 * - ❌ Making structural elements draggable (no dedicated grab surface)
 * - ❌ Relying on pointer-events-none (prevents drag from working)
 *
 * @see /docs/conversation_history/2025/11/2025-11-15.md - Full implementation history
 */
import type { ReactElement } from "react";
import { Filter, Globe, Search } from "lucide-react";
import React, { useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useFilterPanel } from "../../pages/browse/contexts/FilterPanelContext";
import { SearchInput } from "../../pages/search/components/SearchInput";
import { useGlobalKeyboardShortcuts } from "../hooks/useGlobalKeyboardShortcuts";
import { useTransitionNavigation } from "../hooks/useTransitionNavigation";
import { pageTransitions } from "../transitions/pageTransitions";
import { LumiLogo } from "./branding/LumiLogo";
import { UserProfileDropdown, type UserProfileDropdownHandle } from "./UserProfileDropdown";

export type HeaderProps = Readonly<{
  isVisible?: boolean;
  className?: string;
}>;

export function Header({ isVisible = true, className = "" }: HeaderProps): ReactElement {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const { user, loading } = useAuth();
  const location = useLocation();
  const { transitionTo } = useTransitionNavigation();

  // Refs for controlling dropdowns via keyboard shortcuts
  const userProfileDropdownRef = useRef<UserProfileDropdownHandle>(null);

  // Auto-hide header on player pages
  const isPlayerPage = location.pathname.includes("/watch/");
  const shouldShow = isVisible && !isPlayerPage;

  // Detect if we're on the GroupedBrowsePage to show filter icon instead of globe
  const isBrowsePage = location.pathname.includes("/browse");

  // Access filter panel context (returns no-op if not available)
  const filterPanel = useFilterPanel();

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Stable navigation handlers (prevent event handler orphaning during View Transitions)
  const handleNavigateHome = useCallback(() => {
    transitionTo("/home", pageTransitions.fullScreenFade);
  }, [transitionTo]);

  const handleNavigateBrowse = useCallback(() => {
    transitionTo("/home/browse", pageTransitions.slideHorizontal);
  }, [transitionTo]);

  // Global keyboard shortcuts
  useGlobalKeyboardShortcuts({
    onOpenSearch: () => setSearchOpen(true),
    onToggleBrowseMenu: () => {
      // If on browse page, toggle filter panel; otherwise navigate
      if (isBrowsePage) {
        filterPanel.open();
      } else {
        handleNavigateBrowse();
      }
    },
    onToggleUserMenu: () => userProfileDropdownRef.current?.toggle(),
    onNavigateHome: handleNavigateHome,
  });

  if (!shouldShow) {
    return <></>;
  }

  if (!isVisible) return <></>;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 safe-top pointer-events-auto ${className}`}>
      {/* Background gradient */}
      <div
        className="bg-gradient-to-b from-black/90 to-black/60 h-full w-full absolute inset-0 transition-opacity duration-700 ease-out pointer-events-none"
        style={{
          opacity: isScrolled ? 1 : 0,
        }}
      />

      <div className="w-full container-padding h-16 pt-1 flex items-center justify-between relative z-20 max-w-none">
        {/* Left section - logo and nav */}
        <div className="flex items-center gap-4 sm:gap-8 electron-no-drag">
          {/* 
            Spacer for macOS traffic lights - only visible on Electron.
            CRITICAL: Uses inline style for WebkitAppRegion to ensure highest CSS specificity.
            This prevents focus-dependent CSS conflicts that would break dragging.
          */}
          <div className="electron-traffic-light-spacer" />
          <LumiLogo onClick={handleNavigateHome} className="h-4 sm:h-6 pl-4 cursor-pointer" ariaLabel="Go to home" />
          <nav className="hidden md:flex items-center gap-4 lg:gap-6 text-sm font-medium">
            {/* Browse button - shows Filter icon on browse page, Globe icon elsewhere */}
            {isBrowsePage ? (
              <button
                onClick={filterPanel.open}
                className="touch-target text-sm font-display hover:text-lumi-accent transition-all duration-200 rounded-full hover:scale-110"
                aria-label="Open filters"
                type="button"
              >
                <Filter className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleNavigateBrowse}
                className="touch-target text-sm font-display hover:text-lumi-accent transition-all duration-200 rounded-full hover:scale-110"
                aria-label="Browse content"
                type="button"
              >
                <Globe className="w-5 h-5" />
              </button>
            )}
          </nav>
        </div>

        {/* 
          Draggable spacer between logo/nav and right section buttons.
          CRITICAL PATTERN: This flex-1 spacer provides dedicated draggable surface area.
          - flex-1: Expands to fill available space between left and right sections
          - min-w-[40px]: Ensures minimum draggable area even on small screens
          - Inline style WebkitAppRegion: 'drag' for highest CSS specificity
          - Alternative approaches (absolute overlays, pointer-events manipulation) blocked button clicks
        */}
        <div className="flex-1 min-w-[40px] h-16 -my-1" style={{ WebkitAppRegion: "drag" } as React.CSSProperties} />

        {/* Right section - search and profile */}
        <div className="flex items-center gap-4 electron-no-drag">
          {/* Search Button */}
          <button
            onClick={() => setSearchOpen(true)}
            className="touch-target hover:text-lumi-accent rounded-md transition-all hover:scale-110"
            aria-label="Search"
          >
            <Search className="w-5 h-5" />
          </button>

          {!loading &&
            (user ? (
              <UserProfileDropdown ref={userProfileDropdownRef} />
            ) : (
              <Link to="/sign-in" className="text-sm font-medium hover:text-neutral-300 transition-colors">
                Sign In
              </Link>
            ))}
        </div>

        {/* Search Overlay */}
        <SearchInput open={searchOpen} onOpenChange={setSearchOpen} />
      </div>
    </header>
  );
}
